/** Matches DB enum `fund_opportunity_status_type` (no FUND_ESTABLISHED; see remove-fund-established-from-status-enum migration). */
export type OpportunityStatus =
  | 'NEW'
  | 'APPROVED'
  | 'EOI'
  | 'APPLICATION_OPEN'
  | 'UNDER_MANAGEMENT'
  | 'COMPLETE';

export interface StorageItem {
  kind: string;
  id: string;
  selfLink: string;
  mediaLink: string;
  name: string;
  bucket: string;
  generation: string;
  metageneration: string;
  contentType: string;
  storageClass: string;
  size: string;
  md5Hash: string;
  contentLanguage: string;
  crc32c: string;
  etag: string;
  timeCreated: string;
  updated: string;
  timeStorageClassUpdated: string;
}

export interface StorageListResponse {
  items: StorageItem[];
  nextPageToken?: string;
}

export interface CompletedShareData {
  firstName: string;
  lastName: string;
  dob: string;
  mobile: string;
  country: string;
  city: string;
  password: string;
}

export interface PairingStartResponse {
  pairingId: string;
  pollingToken: string;
  expiresAt: string;
  qrData?: any;
}

export interface PairingStatusResponse {
  status:
    | 'pending'
    | 'scanned'
    | 'challenge_required'
    | 'challenge_verified'
    | 'redeemed'
    | 'expired'
    | 'denied';
  correctChoice?: string;
  choices?: string[];
  deviceInfo?: {
    platform?: string;
    userAgent?: string;
    timestamp?: string;
    appVersion?: string;
    deviceModel?: string;
    osVersion?: string;
    deviceName?: string;
  };
  transferGrantId?: string;
  expiresAt: string;
}

/** Legacy interface for backwards compatibility */
export interface CrossDeviceAuthStartResponse {
  pairingId: string;
  ticketUrl: string;
  shortCode: string;
  expiresAt: string;
  qrData?: any;
}

export interface BaseStructuredError {
  structuredError?: boolean;
}

export interface GenericStructuredError extends BaseStructuredError {
  error: string;
}

export interface MissingFieldsError extends BaseStructuredError {
  missingFields: string[];
}

export interface DuplicateFieldsError extends BaseStructuredError {
  duplicateFields: string[];
}

export interface GenericError extends BaseStructuredError {
  error: string;
}

export interface InvalidFieldsError extends BaseStructuredError {
  invalidFields: {
    [key: string]: string;
  };
}

export interface MissingContentError extends BaseStructuredError {
  error: string;
  code: 'MISSING_CONTENT';
}

export interface AiInputInvalidError extends BaseStructuredError {
  error: string;
  code: 'AI_INPUT_INVALID';
}

export interface MissingPDFError extends BaseStructuredError {
  error: string;
  code: 'MISSING_PDF';
}

export interface MissingCommittedInvestmentsError extends BaseStructuredError {
  error: string;
  code: 'MISSING_COMMITTED_INVESTMENTS';
}

export type PromotionPrerequisiteIssueCode =
  | 'MISSING_CONTENT'
  | 'MISSING_PDF'
  | 'MISSING_SUMMARY_IMAGE'
  | 'MISSING_IRR_UPDATE'
  | 'MISSING_COMMITTED_INVESTMENTS'
  | 'FUND_STATUS_REQUIRES_OVERRIDE';

export interface PromotionPrerequisiteIssue {
  issue: PromotionPrerequisiteIssueCode;
  overridable: boolean;
  overrideRequestParam?: string;
  context?: {
    fundStatus?: string;
    investorExternalId?: string;
    operation?: string;
  };
  message?: string;
}

export interface PromotionPrerequisitesError extends BaseStructuredError {
  code: 'PROMOTION_PREREQUISITES';
  error: string;
  issues: PromotionPrerequisiteIssue[];
}

export type StructuredError =
  | MissingFieldsError
  | DuplicateFieldsError
  | InvalidFieldsError
  | MissingContentError
  | MissingPDFError
  | AiInputInvalidError
  | MissingCommittedInvestmentsError
  | PromotionPrerequisitesError
  | GenericStructuredError
  | GenericError;

export type FieldErrorOverride = { [field: string]: string };
type FieldErrorOverrideKey = 'missingFields' | 'duplicateFields' | 'invalidFields';

type FieldErrorOverrides = { [key in FieldErrorOverrideKey]?: FieldErrorOverride };

export const structuredErrorToFieldErrorPairs = (
  error: StructuredError,
  overrideMessages?: FieldErrorOverrides,
): { [key: string]: string } => {
  if ((error as MissingFieldsError).missingFields) {
    return (error as MissingFieldsError).missingFields.reduce(
      (acc, field) => ({
        ...acc,
        [field]: overrideMessages?.missingFields?.[field] || 'This field is required',
      }),
      {},
    );
  }
  if ((error as DuplicateFieldsError).duplicateFields) {
    return (error as DuplicateFieldsError).duplicateFields.reduce(
      (acc, field) => ({
        ...acc,
        [field]: overrideMessages?.duplicateFields?.[field] || 'This field is already in use',
      }),
      {},
    );
  }
  if ((error as InvalidFieldsError).invalidFields) {
    const processedErrors = { ...(error as InvalidFieldsError).invalidFields };

    Object.keys(processedErrors).forEach((key) => {
      processedErrors[key] = overrideMessages?.invalidFields?.[key] || processedErrors[key];
    });

    return processedErrors;
  }
  return {};
};
